import * as React from "react";
import * as DialogPrimitive from "@radix-ui/react-dialog";
import { X } from "lucide-react";
import { cn } from "@/lib/utils";

type Props = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  title?: React.ReactNode;
  description?: React.ReactNode;
  children?: React.ReactNode;
  footer?: React.ReactNode;
  /** Accessible name when no visible `title` is rendered. */
  srTitle?: string;
  /** Max width utility class (default max-w-lg). */
  widthClassName?: string;
  className?: string;
  /** Hide the corner close affordance (confirm dialogs that own their buttons). */
  showCloseButton?: boolean;
  /** Skip the scrolling body wrapper for panels that manage their own layout. */
  bare?: boolean;
  /** Extra classes for the scrolling body wrapper. */
  bodyClassName?: string;
  /** Forwarded to Radix — prevent to lock dismissal (e.g. the welcome carousel). */
  onInteractOutside?: DialogPrimitive.DialogContentProps["onInteractOutside"];
  onEscapeKeyDown?: DialogPrimitive.DialogContentProps["onEscapeKeyDown"];
  /** Forwarded to Radix — prevent when the first tabbable control opens itself
   *  on focus (a combobox), which would pop its menu the moment the modal opens. */
  onOpenAutoFocus?: DialogPrimitive.DialogContentProps["onOpenAutoFocus"];
};

/** Centered modal dialog, styled to the prototype's dialog surface (radius-lg,
 *  shadow-xl, scrim overlay). Radix supplies focus-trap / a11y.
 *
 *  DELIBERATELY NOT a full-screen sheet on phones, which AITS-501's ticket text
 *  asked for. The aits-2.0 mock keeps `.report-modal .report-dialog` a centred
 *  `width: 520px; max-width: 100%` card inside a 28px-padded scrim at every
 *  width — there is no phone breakpoint on it. Every dialog in the app already
 *  matches that, so the sheet would have been a redesign of a dozen surfaces on
 *  the strength of a ticket line the requester flagged as stale. `Drawer` is the
 *  one that does go edge-to-edge on phones (`w-full sm:w-[520px]`), because the
 *  mock's drawer does.
 *
 *  Spacing is on the 4-base scale throughout: gap-4 / p-6 on the panel, gap-1 in
 *  the header stack, gap-2 in the footer. (`.vb-panel-hd`'s 16px/18px is the
 *  mock's own `.report-hd` value and is left alone.) */
export function Modal({
  open,
  onOpenChange,
  title,
  description,
  children,
  footer,
  srTitle = "Dialog",
  widthClassName = "max-w-lg",
  className,
  showCloseButton = true,
  bare = false,
  bodyClassName,
  onInteractOutside,
  onEscapeKeyDown,
  onOpenAutoFocus,
}: Props) {
  return (
    <DialogPrimitive.Root open={open} onOpenChange={onOpenChange}>
      <DialogPrimitive.Portal>
        <DialogPrimitive.Overlay className="vb-overlay z-50 data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0" />
        <DialogPrimitive.Content
          onInteractOutside={onInteractOutside}
          onEscapeKeyDown={onEscapeKeyDown}
          onOpenAutoFocus={onOpenAutoFocus}
          className={cn(
            "vb-root vb-modal-panel fixed left-1/2 top-1/2 z-50 flex max-h-[85vh] w-[calc(100%-2rem)] -translate-x-1/2 -translate-y-1/2 flex-col gap-4 p-6",
            "data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95",
            widthClassName,
            className,
          )}
        >
          {title ? (
            <div className="flex flex-col gap-1 pr-8">
              <DialogPrimitive.Title className="text-base font-bold text-vb-text">
                {title}
              </DialogPrimitive.Title>
              {description && (
                <DialogPrimitive.Description className="text-[12.5px] text-vb-text-2">
                  {description}
                </DialogPrimitive.Description>
              )}
            </div>
          ) : (
            <>
              <DialogPrimitive.Title className="sr-only">{srTitle}</DialogPrimitive.Title>
              {description && (
                <DialogPrimitive.Description className="pr-8 text-[12.5px] text-vb-text-2">
                  {description}
                </DialogPrimitive.Description>
              )}
            </>
          )}

          {/* No body wrapper when there is nothing to put in it — an empty
              flex-1 div would still consume the panel's gap (confirm dialogs
              are title + footer only). */}
          {bare
            ? children
            : children != null && (
                <div className={cn("min-h-0 flex-1 overflow-y-auto", bodyClassName)}>{children}</div>
              )}

          {footer && <div className="flex justify-end gap-2">{footer}</div>}

          {showCloseButton && (
            <DialogPrimitive.Close
              className="absolute right-4 top-4 grid h-7 w-7 place-items-center rounded-vb-sm text-vb-text-3 transition-colors hover:bg-vb-bg-subtle hover:text-vb-text focus:outline-none focus-visible:ring-[3px] focus-visible:ring-vb-focus"
              aria-label="Close"
            >
              <X className="h-4 w-4" />
            </DialogPrimitive.Close>
          )}
        </DialogPrimitive.Content>
      </DialogPrimitive.Portal>
    </DialogPrimitive.Root>
  );
}

export default Modal;
